import { Routes, Route, useNavigate } from "react-router-dom";
import { Typography, Box } from "@mui/material";
import { useEffect, useState } from "react";
import GGButton from "../../../CommonComponents/GGButton/GGButton";
import './MyGigsPanel.scss';
import AddIcon from "@mui/icons-material/Add";
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { GigEditingPage } from "./GigEditingPage";
import { AddNewGigPage } from "./AddNewGigPage";
import AddNewGigPaymentsPage from "./AddNewGigSteps/AddNewGigPaymentsPage";
import AddNewGigUndertakingPage from "./AddNewGigSteps/AddNewGigUndertakingPage";
import { GigAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/GigAPIHandler";
import { SessionManager } from "../../../../services/SessionManager/SessionManager";
import { Gig } from "../../../../Models/Gig";
import { ErrorLogger } from "../../../../utils/Logger";
import GGLoaderView from "../../../UtilityComponents/GGLoaderView/GGLoaderView";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";

const GigCard = ({ gig }: { gig: Gig }) => {
    const navigate = useNavigate();

    const handleEditClick = () => {
        // Navigate to edit page with actual gig data in state
        navigate(`/freelancer-dashboard/my-gigs/edit/${gig.id}`, {
            state: {
                selectedGig: gig
            }
        });
    };

    // Display minimum price from the gig
    const displayPrice = `₹ ${gig.minPrice}`;

    // Get total orders count
    const totalOrders = gig.gigMetrics?.totalOrders ?? 0;

    return (
        <Box className="gig-card">
            <div className="gig-card-image">
                <img 
                    src={gig.coverImg || "https://via.placeholder.com/400x200"} 
                    alt={gig.title}
                    onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        (e.target as HTMLImageElement).src = "https://via.placeholder.com/400x200";
                    }}
                />
            </div>
            <div className="gig-card-content">
                <Typography className="gig-card-title" variant="h6">
                    {gig.title}
                </Typography>
                <Typography className="gig-card-description" variant="body2">
                    {gig.description || "No description available."}
                </Typography>
                <div className="gig-card-stats">
                    <Typography className="gig-card-price" variant="body2">
                        {displayPrice}
                    </Typography>
                    <div className="gig-card-orders">
                        <Inventory2RoundedIcon className="orders-icon" />
                        <Typography variant="body2">
                            {totalOrders} {totalOrders === 1 ? 'order' : 'orders'}
                        </Typography>
                    </div>
                </div>
                <div className="gig-card-actions">
                    <GGButton
                        disableClickShrink
                        variant="outlined"
                        className="edit-button"
                        startIcon={<VisibilityRoundedIcon />}
                        size="small"
                        onClick={handleEditClick}
                    >
                        View/Edit
                    </GGButton>
                    {/* <IconButton className="delete-button" size="small">
                        <DeleteIcon />
                    </IconButton> */}
                </div>
            </div>
        </Box>
    );
};

// Main gigs list component
const MyGigsList = () => {
    const navigate = useNavigate();
    const [myGigList, setMyGigList] = useState<Gig[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [disableAddGig, setDisableAddGig] = useState(false);

    useEffect(() => {
        const fetchGigs = async () => {
            if (SessionManager.shared.buyer && SessionManager.shared.buyer.seller) {
                setIsLoading(true);
                try {
                    const gigs = await GigAPIHandler.shared.getGigsBySeller(SessionManager.shared.buyer.seller);
                    setMyGigList(gigs);
                } catch (err) {
                    ErrorLogger.log(err, 'MyGigsList.fetchGigs');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchGigs();
    }, []);

    useEffect(() => {
        // Disable add gig button if user already has 2 or more gigs
        if (myGigList.length >= 2) {
            setDisableAddGig(true);
        } else {
            setDisableAddGig(false);
        }
    }, [myGigList]);

    const handleAddNewGigClick = () => {
        if (!disableAddGig) {
            navigate("/freelancer-dashboard/my-gigs/add-new-gig");
        }
    };

    return (
        <section className="my-gigs-panel">
            <div className="my-gigs-panel-header">
                <Typography>Manage your Gigs like a Pro</Typography>
                <GGButton 
                    variant="outlined" 
                    btnColor="#0066b2" 
                    startIcon={<AddIcon />}
                    disabled={disableAddGig}
                    onClick={handleAddNewGigClick}
                >
                    Add New Gig
                </GGButton>
            </div>
            <div className="my-gigs-panel-content">
                {isLoading ? (
                    <GGLoaderView />
                ) : myGigList.length === 0 ? (
                    <div className="empty-state">
                        <Typography variant="h5" sx={{ color: "darkgray", textAlign: "center" }}>
                            You do not have any gigs yet. Add one now!
                        </Typography>
                    </div>
                ) : (
                    <div className="gigs-grid">
                        {myGigList.map((gig) => (
                            <GigCard key={gig.id} gig={gig} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};


export const MyGigsPanel = () => {
    return (
        <Routes>
            <Route index element={<MyGigsList />} />
            <Route path="edit/:gigId" element={<GigEditingPage />} />
            <Route path="add-new-gig" element={<AddNewGigPage />} />
            <Route path="add-new-gig/payments" element={<AddNewGigPaymentsPage />} />
            <Route path="add-new-gig/undertaking" element={<AddNewGigUndertakingPage />} />
        </Routes>
    );
};

